import { scaleLinear } from '@visx/scale';

export const generateDomain = (suggestedDomain, range) => {
  if (suggestedDomain.length === range.length) {
    return suggestedDomain;
  }

  // TODO: if they aren't the same length, interpolate the difference
  // HACK: for now, the likely difference is a domain of 2 and range of 3, so we'll only handle that interpolation
  const [min, max] = suggestedDomain;
  const incrementCount = range.length - 1;
  const increment = (max - min) / incrementCount;
  return new Array(range.length).fill(0).map((_, i) => min + i * increment);
};

export const standardColor = () => '#28b3d1';

export const scaleColorTraffic = scaleLinear({
  domain: [0, 0.5, 1],
  range: ['#ed2228', '#fae314', '#4c9745'],
});

/** Dark sibling of the traffic ramp, used as wash ink and as one ink candidate. */
export const scaleColorTrafficInkDark = scaleLinear({
  domain: [0, 0.5, 1],
  range: ['#a50409', '#756900', '#2c6a26'],
});

/** Light sibling of the traffic ramp, used as the other solid-fill ink candidate. */
export const scaleColorTrafficInkLight = scaleLinear({
  domain: [0, 0.5, 1],
  range: ['#ffffff', '#ffffff', '#eaf1ea'],
});

function clamp01(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

/**
 * A 0..1 traffic fraction, or one of the three traffic-stop hexes. Interpolated
 * fills should pass the fraction they were drawn at rather than the rgb string.
 */
export function trafficFraction(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? clamp01(value) : null;
  }

  const hex = toHexColor(value);
  if (!hex) {
    return null;
  }

  const stops = [
    [toHexColor(scaleColorTraffic(0)), 0],
    [toHexColor(scaleColorTraffic(0.5)), 0.5],
    [toHexColor(scaleColorTraffic(1)), 1],
  ];
  const match = stops.find(([color]) => color === hex);
  return match ? match[1] : null;
}

// NOTE: These colors are defined in _palettes.scss
const categoryPalette = [
  { b: '#9d4c8e', c: '#f6e9f4' },
  { b: '#4c9745', c: '#f2fcf1' },
  { b: '#28b3d1', c: '#f6feff' },
  { b: '#f0dd2b', c: '#3d3700' },
  { b: '#ea9727', c: '#f7f3ed' },
  { b: '#4643c8', c: '#ebeaf7' },
  { b: '#ed2228', c: '#fff2f2' },
  { b: '#712563', c: '#f6e9f4' },
  { b: '#25691E', c: '#f2fcf1' },
  { b: '#02758d', c: '#f6feff' },
  { b: '#bcaa03', c: '#3d3700' },
  { b: '#9c5c07', c: '#f7f3ed' },
  { b: '#17148c', c: '#ebeaf7' },
  { b: '#a00f14', c: '#fff2f2' },
  { b: '#ED87DA', c: '#34032b' },
  { b: '#88DA81', c: '#063901' },
  { b: '#70dbf2', c: '#023541' },
  { b: '#f7eb7e', c: '#3d3700' },
  { b: '#fbb65a', c: '#3b2200' },
  { b: '#7e7be0', c: '#010022' },
  { b: '#fc8a8e', c: '#240001' },
];

export const getCategoryPaletteColor = (index) => {
  const { b, c } = categoryPalette[index % categoryPalette.length];
  return { background: b, color: c };
};

export const COLOR_COUNT = 21;

// NOTE: Length of $chart-colors in _palettes.scss (.palette-chart-1 … N)
export const CHART_PALETTE_COUNT = 18;
export const COMBINED_CHART_PALETTE_INDEX = 1;

/**
 * Maps a 0-based breakout cohort index onto .palette-chart-N.
 * Index 1 is reserved for Combined; remaining colors wrap from 2.
 */
export function cohortChartPaletteIndex(cohortIndex) {
  return 2 + (cohortIndex % (CHART_PALETTE_COUNT - 1));
}

export function nextColorIndex(usedColors, totalColors = COLOR_COUNT) {
  for (let i = 0; i < totalColors; i++) {
    if (!usedColors.includes(i)) {
      return i;
    }
  }
  return Math.floor(Math.random() * COLOR_COUNT);
}

const RGB_PATTERN = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i;

function channelToHex(channel) {
  return Math.max(0, Math.min(255, Math.round(Number(channel))))
    .toString(16)
    .padStart(2, '0');
}

/**
 * Canonicalizes a color to `#rrggbb`. Interpolating scales such as
 * `scaleColorTraffic` return `rgb(...)` strings, while every palette in this
 * codebase is written as hex, so callers need one form to compare against.
 */
export function toHexColor(color) {
  if (typeof color !== 'string') {
    return null;
  }

  const rgb = RGB_PATTERN.exec(color.trim());
  if (rgb) {
    const [, r, g, b] = rgb;
    return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
  }

  const normalized = normalizeHexColor(color);
  return normalized ? `#${normalized}` : null;
}

function normalizeHexColor(color) {
  if (typeof color !== 'string') {
    return null;
  }

  const rgb = RGB_PATTERN.exec(color.trim());
  if (rgb) {
    const [, r, g, b] = rgb;
    return `${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
  }

  const trimmed = color.trim().toLowerCase();
  if (trimmed === 'white') {
    return 'ffffff';
  }
  if (trimmed === 'black') {
    return '000000';
  }

  const hex = trimmed.replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    return hex
      .split('')
      .map((ch) => `${ch}${ch}`)
      .join('')
      .toLowerCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    return hex.toLowerCase();
  }
  return null;
}

function relativeLuminance(channel) {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

function luminanceFromHex(color) {
  const normalized = normalizeHexColor(color);
  if (!normalized) {
    return null;
  }

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);

  return (
    0.2126 * relativeLuminance(r) +
    0.7152 * relativeLuminance(g) +
    0.0722 * relativeLuminance(b)
  );
}

function contrastRatio(colorA, colorB) {
  const lumA = luminanceFromHex(colorA);
  const lumB = luminanceFromHex(colorB);

  if (lumA == null || lumB == null) {
    return null;
  }

  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);

  return (lighter + 0.05) / (darker + 0.05);
}

function hexToRgba(hex, alpha) {
  const normalized = normalizeHexColor(hex);
  if (!normalized) {
    return hex;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * Returns a readable foreground color for a background hex color.
 * Defaults to white text, but switches to dark text when white contrast is low.
 * Contrast is evaluated using opaque `light` / `dark`; when `foregroundAlpha` < 1,
 * the returned string uses `rgba(...)` so the tile color shows through slightly.
 */
export function getReadableTextColor(
  backgroundColor,
  {
    light = '#ffffff',
    dark = '#212529',
    minContrast = 4.5,
    foregroundAlpha = 1,
  } = {}
) {
  const whiteContrast = contrastRatio(backgroundColor, light);
  const chosen =
    whiteContrast == null ? dark : whiteContrast >= minContrast ? light : dark;

  if (foregroundAlpha >= 1) {
    return chosen;
  }
  return hexToRgba(chosen, foregroundAlpha);
}

function clampChannel(value) {
  return Math.min(255, Math.max(0, value));
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((channel) => clampChannel(channel).toString(16).padStart(2, '0'))
    .join('')}`;
}

function hexToRgbChannels(color) {
  const hex = toHexColor(color);
  if (!hex) {
    return null;
  }

  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function rgbToHsl(r, g, b) {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const lightness = (max + min) / 2;

  if (delta === 0) {
    return { h: 0, s: 0, l: lightness };
  }

  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let hue;
  if (max === red) {
    hue = (green - blue) / delta + (green < blue ? 6 : 0);
  } else if (max === green) {
    hue = (blue - red) / delta + 2;
  } else {
    hue = (red - green) / delta + 4;
  }

  return { h: hue * 60, s: saturation, l: lightness };
}

function hueToRgb(p, q, t) {
  let tone = t;
  if (tone < 0) {
    tone += 1;
  }
  if (tone > 1) {
    tone -= 1;
  }
  if (tone < 1 / 6) {
    return p + (q - p) * 6 * tone;
  }
  if (tone < 1 / 2) {
    return q;
  }
  if (tone < 2 / 3) {
    return p + (q - p) * (2 / 3 - tone) * 6;
  }
  return p;
}

function hslToRgb(h, s, l) {
  if (s === 0) {
    const value = Math.round(l * 255);
    return [value, value, value];
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = h / 360;

  return [
    Math.round(hueToRgb(p, q, hue + 1 / 3) * 255),
    Math.round(hueToRgb(p, q, hue) * 255),
    Math.round(hueToRgb(p, q, hue - 1 / 3) * 255),
  ];
}

/**
 * Sass `adjust-color($color, $lightness:, $saturation:)` — adds the given
 * percentage points and clamps each channel to 0–100.
 *
 * Darkening `$yellow` (`#fae314`) by 30 lightness / +15 saturation is how
 * `$inverse-yellow` becomes `#756900`.
 */
export function adjustColor(color, { lightness = 0, saturation = 0 } = {}) {
  const rgb = hexToRgbChannels(color);
  if (!rgb) {
    return null;
  }

  const hsl = rgbToHsl(...rgb);
  const nextSaturation = Math.min(100, Math.max(0, hsl.s * 100 + saturation));
  const nextLightness = Math.min(100, Math.max(0, hsl.l * 100 + lightness));

  return rgbToHex(
    ...hslToRgb(hsl.h, nextSaturation / 100, nextLightness / 100)
  );
}

/**
 * Sass `mix($colorA, $colorB, $weight)` for opaque colors. `$weight` is the
 * percent of `$colorA` (so `mix($red, #fff, 40%)` is 40% red / 60% white).
 */
export function mixColors(colorA, colorB, weight = 50) {
  const a = hexToRgbChannels(colorA);
  const b = hexToRgbChannels(colorB);
  if (!a || !b) {
    return null;
  }

  const amount = Number(weight);
  if (!Number.isFinite(amount)) {
    return null;
  }

  const t = Math.min(100, Math.max(0, amount)) / 100;
  return rgbToHex(
    Math.round(a[0] * t + b[0] * (1 - t)),
    Math.round(a[1] * t + b[1] * (1 - t)),
    Math.round(a[2] * t + b[2] * (1 - t))
  );
}

export const WASH_MIX_WEIGHT = 40;

export function fadeColor(color, weight = WASH_MIX_WEIGHT) {
  return mixColors(color, '#ffffff', weight);
}

export function trafficInkDark(fraction) {
  const t = trafficFraction(fraction);
  return t == null ? null : toHexColor(scaleColorTrafficInkDark(t));
}

export function trafficInkLight(fraction) {
  const t = trafficFraction(fraction);
  return t == null ? null : toHexColor(scaleColorTrafficInkLight(t));
}

/**
 * Ink for a washed (faded-toward-white) background. The light sibling that
 * `inkColorFor` picks against a solid fill disappears on a 40% wash, so this
 * is always the dark traffic ink.
 */
export function darkInkColorFor(fractionOrColor) {
  return trafficInkDark(fractionOrColor);
}

/**
 * Foreground ("ink") color for a filled decoration. Looks up the dark and
 * light traffic-ink scales at the same 0..1 stop as the fill, then keeps the
 * one farther away in relative luminance.
 *
 * WCAG contrast *ratio* is the wrong picker here: it inflates dark-on-mid
 * because the denominator shrinks. Absolute luminance gap matches what the
 * glyphs actually need: yellow stays `#756900`, red and green go light.
 */
export function inkColorFor(fractionOrColor, backgroundColor) {
  const fraction = trafficFraction(fractionOrColor);
  if (fraction == null) {
    return null;
  }

  const darkened = trafficInkDark(fraction);
  const lightened = trafficInkLight(fraction);
  const fill =
    toHexColor(backgroundColor) ?? toHexColor(scaleColorTraffic(fraction));

  if (darkened == null && lightened == null) {
    return null;
  }
  if (darkened == null) {
    return lightened;
  }
  if (lightened == null) {
    return darkened;
  }

  const backgroundLum = luminanceFromHex(fill);
  const darkLum = luminanceFromHex(darkened);
  const lightLum = luminanceFromHex(lightened);

  if (backgroundLum == null || darkLum == null || lightLum == null) {
    return darkened;
  }

  const darkGap = Math.abs(backgroundLum - darkLum);
  const lightGap = Math.abs(backgroundLum - lightLum);

  return lightGap > darkGap ? lightened : darkened;
}
